import React, { useEffect, useMemo, useState } from 'react';

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Stack, Typography, useTheme } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';

import { paths } from 'routes';

import { Search } from 'components/Search';
import { BreadCrumbs } from 'components/Breadcrumbs';
import { ContentLayout } from 'components/Layout';
import { NotFound } from 'components/NotFound/NotFound';
import { Pagination } from 'components/Pagination';
import { usePageSize } from 'hook/usePageSize';

import { useGetAll } from '../api/getAll';
import { ListItem } from '../components/ListItem';
import { useAuth } from 'lib/auth';
import { Loader } from 'components/Loader';
import { PlaceRadiusPopover, formatPlaceRadius } from 'components/PlaceAutocomplete/PlaceRadiusPopover';
import { PlaceSearchUnavailableNotice, isPlaceSearchError } from 'components/PlaceAutocomplete/PlaceSearchUnavailableNotice';
import { useCompetenceAreas } from 'api/getCompetenceAreas';
import { MultiSelectDialog } from 'components/Form/MultiSelectDialog';
import { CardAutocomplete } from 'components/Form';
import { getCompanies } from 'features/companies/api/getAllCompanies';
import { useFilters } from 'contexts/FiltersContext';
import { IconWrapper } from 'components/Icon';
import BackgroundImage from '../../../website/assets/background.png';
import { JobAdsMapDialog } from 'components/JobAdsMap/JobAdsMapDialog';
import { isMapConfigured } from 'components/JobAdsMap/mapsConfig';

const CompanyItem = ({ Name, VAT, ...rest }) => (
  <Stack gap={1} alignItems={'flex-start !important'} {...rest}>
    <Typography variant="body1" fontWeight="bold">
      {Name} ({VAT})
    </Typography>
  </Stack>
);

const OWNER_TYPE_OPTIONS = [
  { id: 'Intermediator', label: 'Intermediator' },
  { id: 'Framework', label: 'Framework Agreement' },
  { id: 'DPS', label: 'DPS' },
  { id: 'Direct', label: 'Direct' },
];

export const List = ({isHomePage = false}) => {
  const { filters, setFilters, defaultFilters, isLoaded } = useFilters();
  const { account, user } = useAuth();
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();
  const [search, setSearch] = useState('');
  // const [filters, setFilters] = useState({
  //   includeInactive: false,
  //   includeExpired: false,
  //   sortBy: 'dateCreated',
  //   scope: 'others',
  //   competenceAreas: [],
  //   locations: [],
  // });
  const [loading, setLoading] = useState(true);
  const [timer, setTimer] = useState(null);
  const [isAreasDialogOpen, setIsAreasDialogOpen] = useState(false);
  const [selectedCompetenceAreas , setSelectedCompetenceAreas] = useState([]);
  const [isTypesDialogOpen, setIsTypesDialogOpen] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [isBuyerDialogOpen, setIsBuyerDialogOpen] = useState(false);
  const [draftBuyer, setDraftBuyer] = useState(null);
  const [placeAnchorEl, setPlaceAnchorEl] = useState(null);
  const [isMapOpen, setIsMapOpen] = useState(false);

  const { data: competences, isLoading: isAreasLoading } = useCompetenceAreas();

  const allAreas = useMemo(() => {
    if (!competences?.competences) return [];
    const mapData = (competences) => competences.map(item => ({
      id: item.id,
      label: item.name,
    }));

    return mapData(competences.competences);
  }, [competences]);  

  const handlePlaceApply = ({ place, radiusKm }) => {
    setFilters(prev => ({ ...prev, place, radiusKm }));
  };

  // const handleCompetenceAreasConfirmSelection = (confirmedIds) => {
  //   setFilters(prev => ({ ...prev, competenceAreas: confirmedIds }));
  // };

  const getDefaultScopeForPage = () => {
    if (!isHomePage) return 'others';
    if (account.super_admin) return 'all';
    if (user?.Company?.Type === 'buyer') return 'all';
    return 'others';
  };

  const handleClearAll = () => {
    setFilters({...defaultFilters, scope: getDefaultScopeForPage()});
  }

  // const handleLocationsConfirmSelection = (confirmedSelection) => {
  //   handleFilterUpdate({
  //     target: {
  //       name: 'locations',
  //       value: confirmedSelection,
  //       checked: null,
  //     }
  //   });

  //   setSelectedLocations(confirmedSelection);
  // };

  const handleCompetenceAreasChange = (event) => {
    const { value } = event.target;
    const selectedIds = typeof value === 'string' ? value.split(',') : value;
    const newSelectedValues = allAreas.filter(option => selectedIds.includes(option.id));
    setSelectedCompetenceAreas(newSelectedValues);
  };

  const handleCompetenceAreasConfirmSelection= (value) => {
    const selectedIds = value.map(item => item.id);
    
    handleFilterUpdate({
      target: {
        name: 'competenceAreas',
        value: selectedIds,
        checked: null,
      }
    });

    setSelectedCompetenceAreas(value);
  };

  const handleCompetenceAreasDelete = (deletedId) => (event) => {
    event.stopPropagation();
    const updatedSelectedValues = selectedCompetenceAreas.filter((item) => item.id !== deletedId);
    setSelectedCompetenceAreas(updatedSelectedValues);
  };

  const handleTypesChange = (event) => {
    const { value } = event.target;
    const selectedIds = typeof value === 'string' ? value.split(',') : value;
    const newSelectedValues = OWNER_TYPE_OPTIONS.filter(option => selectedIds.includes(option.id));
    setSelectedTypes(newSelectedValues);
  };

  const handleTypesConfirmSelection = (value) => {
    const selectedIds = value.map(item => item.id);

    handleFilterUpdate({
      target: {
        name: 'ownerTypes',
        value: selectedIds,
        checked: null,
      }
    });

    setSelectedTypes(value);
  };

  const handleTypesDelete = (deletedId) => (event) => {
    event.stopPropagation();
    const updatedSelectedValues = selectedTypes.filter((item) => item.id !== deletedId);
    setSelectedTypes(updatedSelectedValues);
  };

  const handleBuyerChange = (newValue) => {
    setFilters((filters) => ({
      ...filters,
      buyerID: newValue ? newValue.company_id : null,
      buyerName: newValue ? newValue.Name : '',
    }));
  };

  const selectedBuyer = useMemo(() => (
    filters.buyerID
      ? { company_id: filters.buyerID, Name: filters.buyerName, label: filters.buyerName }
      : null
  ), [filters.buyerID, filters.buyerName]);

  const handleBuyerDialogOpen = () => {
    setDraftBuyer(selectedBuyer);
    setIsBuyerDialogOpen(true);
  };

  const handleBuyerClear = () => {
    handleBuyerChange(null);
    setIsBuyerDialogOpen(false);
  };

  const handleBuyerConfirm = () => {
    handleBuyerChange(draftBuyer);
    setIsBuyerDialogOpen(false);
  };

  useEffect(() => {
    const expectedScope = getDefaultScopeForPage();
    if (filters.scope !== expectedScope) {
      handleFilterUpdate({
        target: {
          name: 'scope',
          value: expectedScope,
          checked: null,
        }
      });
    }
  }, [isHomePage, account.super_admin, user?.Company?.Type]);

  useEffect(() => {
    if (isLoaded && allAreas.length > 0) {      
      const value = filters.competenceAreas;
      const selectedIds = typeof value === 'string' ? value.split(',') : value;
      const newSelectedValues = allAreas.filter(option => selectedIds.includes(option.id));
      
      setSelectedCompetenceAreas(newSelectedValues);
    }
  }, [filters.competenceAreas, isLoaded, allAreas])

  useEffect(() => {
    if (isLoaded) {
      const value = filters.ownerTypes;
      const selectedIds = typeof value === 'string' ? value.split(',') : value;
      const newSelectedValues = OWNER_TYPE_OPTIONS.filter(option => selectedIds.includes(option.id));

      setSelectedTypes(newSelectedValues);
    }
  }, [filters.ownerTypes, isLoaded])


  const {
    data: { jobAds, total, searchArea },
    refetch,
    isFetching,
    error: listError,
  } = useGetAll({
    search,
    ...filters,
    page: page - 1,
    limit: pageSize,
    queryKey: [isHomePage ? 'jobAdsAll' : 'jobAds', page, pageSize, search, filters],
    queryOptions: {
      onSettled: () => {
        clearTimeout(timer);
        setTimeout(() => setLoading(false), 1000);
      },
      enabled: isLoaded,
    },
  });  
  const debouncedSearchChange = useDebouncedCallback((search) => setSearch(search), 300);
  const handleSearchChange = ({ target: { value: search } }) => {
    debouncedSearchChange(search);
  };

  const handlePageChange = (_, page) => setPage(page);

  useEffect(() => {
    if (isFetching) {
      const timeoutId = setTimeout(() => setLoading(true), 500);
      setTimer(timeoutId);
    } else {
      clearTimeout(timer);
    }

    return () => clearTimeout(timer);
  }, [isFetching]);

  const handleFilterUpdate = ({ target: { name, value, checked } }) => {
    setFilters((filters) => {
      return { ...filters, [name]: value };
    });
  };

  // const handleClearAll = () => {
  //   handleLocationsConfirmSelection([]);
  //   handleCompetenceAreasConfirmSelection([]);
  // }

  const isLoading = isAreasLoading;

  return (
    <ContentLayout>
      <Stack height={1}>
        <Stack>
          <BreadCrumbs
            items={[
              { name: '', to: paths.myJobAds },
              { name: `Job Advertisements${total > 0 ? ` (${total})` : ''}` }]}
          />
        </Stack>
        <Stack>
          
        </Stack>

        <Stack
          px={4}
          py={2}
          bgcolor="background.paper"
          borderTop="1px solid"
          borderColor="border.main"
          height="100%"
        >
          <Stack direction="column" gap={2.5} alignItems="center" justifyContent={"space-between"}>
            <Search
                placeholder="Search assignments"
                handleSearchChange={handleSearchChange}
                sx={{
                    minWidth: 200,
                    height: 40,
                    py: 2.5,
                    px: 3.5,
                    borderRadius: 1,
                    alignSelf: "stretch",
                    border: "1px solid",
                    borderColor:"#13373F33",
                    backgroundColor: "background.paper",
                    boxShadow: "0 12px 40px 0 rgba(0, 0, 0, 0.08)",
                }}
                inputSx={{
                    border: "none",
                    height: 1,
                    '& .MuiOutlinedInput-input': {
                        fontSize: "1rem",
                        fontWeight: 400,
                        fontFeatureSettings: `'liga' off, 'clig' off`,
                        color: 'text.black',
                        lineHeight: '24px',
                    },
                    '& .MuiInputBase-input::placeholder': {
                        color: "text.placeholder",
                        fontFeatureSettings: '"liga" off, "cling" off',
                        fontSize: '1rem',
                        fontWeight: 400,
                        lineHeight: '24px',
                    },
                    pl: 0,
                    pr: 0,
                }}
                iconSx={{
                    svg: {
                        height: 24,
                        color: 'text.black',
                        stroke: "#000",
                        strokeWidth: 0,
                    }
                }}
            />
            <Stack
              direction={"row"}
              gap={2}
              alignItems="center"
              justifyContent={"space-between"}
              width={1}
            >
              <Stack
                direction={"row"}
                gap={2}
                alignItems={"flex-start"}
                sx={{flexWrap: "wrap"}}
              >
                <Box>
                  <Button
                    variant="outlined"
                    onClick={(event) => setPlaceAnchorEl(event.currentTarget)}
                    disabled={isLoading}
                    sx={{
                      backgroundColor: "background.paper",
                      borderRadius: 1,
                      fontWeight: 400,
                      lineHeight: "24px",
                      fontFeatureSettings: '"liga" off, "cling" off',
                      boxShadow: "0 2px 4px 0 rgba(19, 55, 63, 0.20)",
                      color: 'text.primary',
                      borderColor: 'border.jobAd',
                    }}
                    endIcon={
                      <IconWrapper
                        icon="chevronDown"
                        sx={{
                            height: 14,
                            width: 14,
                            svg: {
                                stroke: "#000",
                                strokeWidth:1,
                                height: 14,
                                width: 14,
                                mt: -1.25,
                            },
                            ml: 1,
                        }}
                        height={14}
                      />
                    }
                  >
                    Location {filters.place && `(${formatPlaceRadius(filters.place, filters.radiusKm)})`}
                  </Button>
                  <PlaceRadiusPopover
                    anchorEl={placeAnchorEl}
                    onClose={() => setPlaceAnchorEl(null)}
                    place={filters.place}
                    radiusKm={filters.radiusKm}
                    onApply={handlePlaceApply}
                  />
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    onClick={() => setIsAreasDialogOpen(true)}
                    disabled={isLoading}
                    sx={{
                      backgroundColor: "background.paper",
                      borderRadius: 1,
                      fontWeight: 400,
                      lineHeight: "24px",
                      fontFeatureSettings: '"liga" off, "cling" off',
                      boxShadow: "0 2px 4px 0 rgba(19, 55, 63, 0.20)",
                      color: 'text.primary',
                      borderColor: 'border.jobAd',
                    }}
                    endIcon={
                      <IconWrapper
                        icon="chevronDown"
                        sx={{
                            height: 14,
                            width: 14,
                            svg: {
                                stroke: "#000",
                                strokeWidth:1,
                                height: 14,
                                width: 14,
                                mt: -1.25,
                            },
                            ml: 1,
                        }}
                        height={14}
                      />
                    }
                  >
                    Competence Areas {selectedCompetenceAreas.length > 0 && `(${selectedCompetenceAreas.length}/${allAreas.length > 0 ? allAreas.length : '...'})`}
                  </Button>
                  <MultiSelectDialog
                    name = 'Comptenece Areas'
                    open={isAreasDialogOpen}
                    onClose={() => setIsAreasDialogOpen(false)}
                    onConfirm={handleCompetenceAreasConfirmSelection}
                    onChange={handleCompetenceAreasChange}
                    onDelete={handleCompetenceAreasDelete}
                    value={selectedCompetenceAreas}
                    treeData={allAreas}
                  />
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    onClick={() => setIsTypesDialogOpen(true)}
                    disabled={isLoading}
                    sx={{
                      backgroundColor: "background.paper",
                      borderRadius: 1,
                      fontWeight: 400,
                      lineHeight: "24px",
                      fontFeatureSettings: '"liga" off, "cling" off',
                      boxShadow: "0 2px 4px 0 rgba(19, 55, 63, 0.20)",
                      color: 'text.primary',
                      borderColor: 'border.jobAd',
                    }}
                    endIcon={
                      <IconWrapper
                        icon="chevronDown"
                        sx={{
                            height: 14,
                            width: 14,
                            svg: {
                                stroke: "#000",
                                strokeWidth:1,
                                height: 14,
                                width: 14,
                                mt: -1.25,
                            },
                            ml: 1,
                        }}
                        height={14}
                      />
                    }
                  >
                    Type {selectedTypes.length > 0 && `(${selectedTypes.length}/${OWNER_TYPE_OPTIONS.length})`}
                  </Button>
                  <MultiSelectDialog
                    name = 'Type'
                    open={isTypesDialogOpen}
                    onClose={() => setIsTypesDialogOpen(false)}
                    onConfirm={handleTypesConfirmSelection}
                    onChange={handleTypesChange}
                    onDelete={handleTypesDelete}
                    value={selectedTypes}
                    treeData={OWNER_TYPE_OPTIONS}
                  />
                </Box>
                <Box>
                  <Button
                    variant="outlined"
                    onClick={handleBuyerDialogOpen}
                    disabled={isLoading}
                    sx={{
                      backgroundColor: "background.paper",
                      borderRadius: 1,
                      fontWeight: 400,
                      lineHeight: "24px",
                      fontFeatureSettings: '"liga" off, "cling" off',
                      boxShadow: "0 2px 4px 0 rgba(19, 55, 63, 0.20)",
                      color: 'text.primary',
                      borderColor: 'border.jobAd',
                    }}
                    endIcon={
                      <IconWrapper
                        icon="chevronDown"
                        sx={{
                            height: 14,
                            width: 14,
                            svg: {
                                stroke: "#000",
                                strokeWidth:1,
                                height: 14,
                                width: 14,
                                mt: -1.25,
                            },
                            ml: 1,
                        }}
                        height={14}
                      />
                    }
                  >
                    Buyer {filters.buyerName && `(${filters.buyerName})`}
                  </Button>
                  <Dialog open={isBuyerDialogOpen} onClose={() => setIsBuyerDialogOpen(false)} fullWidth maxWidth="sm">
                    <DialogTitle>Buyer</DialogTitle>
                    <DialogContent>
                      <CardAutocomplete
                        name="buyerFilter"
                        label="Select Buyer"
                        queryFunction={(search) => getCompanies('buyer', 15, true, search)}
                        optionLabelName="Name"
                        ItemCard={CompanyItem}
                        onChange={setDraftBuyer}
                        value={draftBuyer}
                        skipCreatingShadow={true}
                        fetchOnOpen={true}
                        fullWidth
                        changeSelectedValueOnChange={true}
                      />
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={handleBuyerClear}>Clear</Button>
                      <Button onClick={() => setIsBuyerDialogOpen(false)}>Cancel</Button>
                      <Button onClick={handleBuyerConfirm} variant="contained">Confirm</Button>
                    </DialogActions>
                  </Dialog>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => handleFilterUpdate({ target: { name: 'starred', value: !filters.starred } })}
                  disabled={isLoading}
                  aria-pressed={!!filters.starred}
                  sx={{
                    backgroundColor: "background.paper",
                    borderRadius: 1,
                    fontWeight: 400,
                    lineHeight: "24px",
                    fontFeatureSettings: '"liga" off, "cling" off',
                    boxShadow: "0 2px 4px 0 rgba(19, 55, 63, 0.20)",
                    color: 'text.primary',
                    borderColor: filters.starred ? 'warning.main' : 'border.jobAd',
                  }}
                  startIcon={
                    <IconWrapper
                      icon={filters.starred ? 'star' : 'starOutline'}
                      sx={{
                          height: 16,
                          width: 16,
                          svg: {
                              color: filters.starred ? theme.palette.warning.main : theme.palette.grey[500],
                              stroke: filters.starred ? theme.palette.warning.main : theme.palette.grey[500],
                              strokeWidth: filters.starred ? 0 : 1.5,
                              height: 16,
                              width: 16,
                              mt: -1,
                          },
                      }}
                      height={16}
                    />
                  }
                >
                  Starred
                </Button>
                {isMapConfigured() && jobAds?.length > 0 && (
                  <Button
                    variant="outlined"
                    onClick={() => setIsMapOpen(true)}
                    disabled={isLoading}
                    startIcon={
                      <IconWrapper
                        icon="map"
                        sx={{
                            height: 16,
                            width: 16,
                            svg: {
                                stroke: "#000",
                                strokeWidth: 1.5,
                                height: 16,
                                width: 16,
                                mt: -1,
                            },
                        }}
                        height={16}
                      />
                    }
                    sx={{
                      backgroundColor: "background.paper",
                      borderRadius: 1,
                      fontWeight: 400,
                      lineHeight: "24px",
                      fontFeatureSettings: '"liga" off, "cling" off',
                      boxShadow: "0 2px 4px 0 rgba(19, 55, 63, 0.20)",
                      color: 'text.primary',
                      borderColor: 'border.jobAd',
                    }}
                  >
                    Show on map
                  </Button>
                )}
                <JobAdsMapDialog
                  open={isMapOpen}
                  onClose={() => setIsMapOpen(false)}
                  jobAds={jobAds ?? []}
                  searchArea={searchArea}
                  getAdUrl={(ad) => paths.jobAdsDetailsView(ad.ad_id)}
                />
                <Button
                  variant="outlined"
                  onClick={handleClearAll}
                  disabled={isLoading}
                  startIcon={
                    <IconWrapper
                      icon="xMark"
                      sx={{
                          height: 14,
                          width: 14,
                          svg: {
                              stroke: "#000",
                              strokeWidth:1,
                              height: 14,
                              width: 14,
                              mt: -1.25,
                          },
                          mr: 1,
                      }}
                        height={14}
                      />
                  }
                  sx={{
                    backgroundColor: "background.light",
                    borderRadius: 1,
                    fontWeight: 400,
                    lineHeight: "24px",
                    fontFeatureSettings: '"liga" off, "cling" off',
                    boxShadow: "0 2px 4px 0 rgba(19, 55, 63, 0.20)",
                    color: 'text.primary',
                    borderColor: 'border.jobAd',
                  }}
                >
                  Clear All
                </Button>
              </Stack>
            </Stack>
          </Stack>

          {filters.place && isPlaceSearchError(listError) && (
            <PlaceSearchUnavailableNotice
              message="Location search is temporarily unavailable."
              clearLabel="Clear location"
              onClear={() => handlePlaceApply({ place: null, radiusKm: filters.radiusKm })}
            />
          )}

          {isFetching && !loading ? 
          (
            <></>
          ) :
          (!isFetching || (isFetching && loading)) ?
          (
            <>
            {loading && (
              <Loader />
            )}

            {!loading && (
            <>
            {!jobAds && (
              <Stack
                height="100%"
                sx={{
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h5">No Job Advertisements found</Typography>
              </Stack>
            )}

            {jobAds && (
              <>
              <Stack
                flex={1}
                mt={2}
                py={2}
                bgcolor="background.paper"
                borderTop="1px solid"
                borderColor="border.main"
                gap={3.5}
              >
                {jobAds.length === 0 && (
                  <NotFound
                    wrapperSx={{ mt: 19 }}
                    title="No Job Advertisements found"
                  />
                )}
                {jobAds.length !== 0 &&
                  jobAds.map((item) => (
                    <ListItem
                      key={item.ad_id}
                      {...item}
                      onStarChange={filters.starred ? () => refetch() : undefined}
                    />
                  ))}
              </Stack>
              </>
            )}

            <Stack direction="row" justifyContent="center">
              <Pagination
                page={page}
                handlePageChange={handlePageChange}
                itemCount={total}
                itemsPerPage={pageSize}
                onItemsPerPageChange={setPageSize}
              />
            </Stack>
            </>
            )}
            </>
          ) :
          (
            <></>
          )}
        </Stack>
      </Stack>
    </ContentLayout>
  );
};
